const KYC_FIELDS = ["panCard", "aadharCard", "bankDetails"];

const LABEL_BY_FIELD = {
  panCard: "PAN details",
  aadharCard: "Aadhaar details",
  bankDetails: "Bank details",
};

const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");

const toNumberSafe = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const hasAtLeastOneImageUrl = (images) => {
  if (!Array.isArray(images)) return false;
  return images.some((img) => {
    if (typeof img === "string") return normalizeString(img) !== "";
    return normalizeString(img?.url) !== "";
  });
};

const hasAtLeastNImageUrls = (images, minCount) => {
  if (!Array.isArray(images)) return false;
  const count = images.reduce((acc, img) => {
    const url = typeof img === "string" ? img : img?.url;
    return normalizeString(url) !== "" ? acc + 1 : acc;
  }, 0);
  return count >= minCount;
};

export const getCoachKycRequirements = (fieldConfigs, coach) => {
  const affiliationType = normalizeString(coach?.affiliationType).toLowerCase();
  const isAcademyAffiliated = affiliationType === "academy";

  const coachShare = toNumberSafe(coach?.coachShare);
  const forcedFullKyc = isAcademyAffiliated && coachShare > 0;

  const requiredSet = new Set();

  if (forcedFullKyc) {
    KYC_FIELDS.forEach((f) => requiredSet.add(f));
  } else {
    const panel = isAcademyAffiliated ? "academy" : "coach";

    (Array.isArray(fieldConfigs) ? fieldConfigs : []).forEach((cfg) => {
      if (!cfg?.field) return;
      if (!KYC_FIELDS.includes(cfg.field)) return;
      if (cfg.requiredFor?.[panel]) requiredSet.add(cfg.field);
    });
  }

  return {
    forcedFullKyc,
    panCard: requiredSet.has("panCard"),
    aadharCard: requiredSet.has("aadharCard"),
    bankDetails: requiredSet.has("bankDetails"),
  };
};

export const getCoachKycMissingFields = (fieldConfigs, coach) => {
  const { panCard, aadharCard, bankDetails } = getCoachKycRequirements(fieldConfigs, coach);

  const missing = [];

  if (panCard) {
    const panNumber = normalizeString(coach?.kycDocuments?.documentNumber);
    const hasPanNumber = panNumber !== "";
    const hasPanImages = hasAtLeastOneImageUrl(coach?.kycDocuments?.documentImg);
    if (!hasPanNumber || !hasPanImages) missing.push("panCard");
  }

  if (aadharCard) {
    const aadhaarNumber = normalizeString(coach?.aadhaarNumber);
    const hasAadhaarNumber = aadhaarNumber !== "";
    const hasAadhaarImages = hasAtLeastNImageUrls(coach?.aadhaarImage, 2);
    if (!hasAadhaarNumber || !hasAadhaarImages) missing.push("aadharCard");
  }

  if (bankDetails) {
    const hasAccountNumber = normalizeString(coach?.bankDetails?.accountNumber) !== "";
    const hasIfsc = normalizeString(coach?.bankDetails?.ifsc) !== "";
    const hasHolderName = normalizeString(coach?.bankDetails?.accountHolderName) !== "";

    if (!hasAccountNumber || !hasIfsc || !hasHolderName) missing.push("bankDetails");
  }

  return missing;
};

export const isCoachKycComplete = (fieldConfigs, coach) => {
  const missing = getCoachKycMissingFields(fieldConfigs, coach);
  return missing.length === 0;
};

export const getCoachKycMissingMessage = (fieldConfigs, coach, options = {}) => {
  const { allMissingGeneric = false } = options;

  const missingFields = getCoachKycMissingFields(fieldConfigs, coach);
  if (missingFields.length === 0) return "";

  if (allMissingGeneric && missingFields.length === KYC_FIELDS.length) {
    return "Missing: KYC details";
  }

  const labels = missingFields.map((f) => LABEL_BY_FIELD[f] || f);
  return `Missing: ${labels.join(", ")}`;
};
